import type { ReactNode, KeyboardEvent } from "react";
import { cn } from "@/cn";
import type { BadgeConfig, MetadataItem } from "./types";

export interface EntityCardProps {
  id: string;
  title: string | undefined;
  description?: string | ReactNode;
  primaryBadge?: BadgeConfig;
  secondaryBadge?: BadgeConfig;
  icon?: ReactNode;
  metadata?: MetadataItem[];
  footerContent?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function EntityCard({
  title,
  description,
  primaryBadge,
  secondaryBadge,
  icon,
  metadata,
  footerContent,
  actions,
  onClick,
  className = "",
}: EntityCardProps) {
  const isInteractive = Boolean(onClick);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-label={isInteractive ? `Open ${title || "Untitled"}` : undefined}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "flex h-full flex-col rounded-lg border border-border bg-card p-4",
        isInteractive &&
          "cursor-pointer transition hover:border-muted hover:bg-muted/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand",
        className,
      )}
    >
      <div className="mb-2 flex-1">
        {(primaryBadge || secondaryBadge) && (
          <div className="mb-2 flex items-start justify-between gap-2">
            {primaryBadge ? (
              <span
                className={cn(
                  "rounded px-2 py-0.5 text-xs",
                  primaryBadge.colorClass,
                )}
              >
                {primaryBadge.label}
              </span>
            ) : (
              <span />
            )}
            {secondaryBadge ? (
              <span
                className={cn(
                  "rounded px-2 py-0.5 text-xs",
                  secondaryBadge.colorClass,
                )}
              >
                {secondaryBadge.label}
              </span>
            ) : null}
          </div>
        )}

        <div className="mb-1 flex items-start gap-2">
          {icon}
          <h3 className="line-clamp-2 flex-1 text-sm font-semibold text-foreground">
            {title || "Untitled"}
          </h3>
        </div>

        {description ? (
          <div className="mb-2 text-xs text-muted-foreground">
            {typeof description === "string" ? (
              <p className="line-clamp-3">{description}</p>
            ) : (
              description
            )}
          </div>
        ) : null}

        {metadata && metadata.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {metadata.map((item, idx) => (
              <div
                key={idx}
                className={cn("flex items-center gap-1", item.className)}
              >
                {item.icon}
                {item.label ? <span>{item.label}:</span> : null}
                <span>{item.value}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {footerContent || actions ? (
        <div className="border-t border-border pt-2">
          {footerContent}
          {actions ? (
            <div className="mt-1 flex items-center justify-end">{actions}</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
